/**
 * Canonical checkbox primitive — the label-wrapping pattern.
 *
 *   <Checkbox label="I agree" checked={…} onChange={…} />
 *   <Checkbox label="Subscribe" description="Weekly newsletter" />
 *
 * For radio buttons, follow the same wrapper structure with type="radio".
 */

import React, { useId } from "react";
import { useTheme } from "../../context/ThemeContext";
import { getPrimaryText, getMutedText } from "../../utils/themeUtils";
import { TOKENS } from "../../utils/designTokens";

interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "size"> {
  /** Required — the text rendered after the box. */
  label: React.ReactNode;
  /** Optional smaller text below the label. */
  description?: React.ReactNode;
}

const Checkbox: React.FC<CheckboxProps> = ({
  label,
  description,
  id: providedId,
  className = "",
  disabled,
  ...rest
}) => {
  const { isDarkMode } = useTheme();
  const generatedId = useId();
  const id = providedId || generatedId;

  return (
    <label
      htmlFor={id}
      className={`flex items-start gap-2 ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"} ${className}`}
    >
      <input
        type="checkbox"
        id={id}
        disabled={disabled}
        className={`${TOKENS.checkbox.base} mt-0.5`}
        {...rest}
      />
      <span className="flex flex-col">
        <span className={`${TOKENS.checkbox.label} ${getPrimaryText(isDarkMode)}`}>
          {label}
        </span>
        {description && (
          <span className={`text-xs mt-0.5 ${getMutedText(isDarkMode)}`}>
            {description}
          </span>
        )}
      </span>
    </label>
  );
};

export default Checkbox;
